/**
 * Workspace overlay-panel identity + single-open toggle logic.
 *
 * The header toolbar opens one panel at a time as a modal overlay. Rather than
 * a boolean-per-panel with hand-rolled mutual-exclusion clearing scattered
 * across every button's onClick (the old shape — error-prone and easy to leave
 * two panels open), a single `WorkspacePanel | null` holds the open panel and
 * {@link nextPanel} computes the next value. Kept pure so the single-open
 * invariant is unit-tested without a DOM.
 *
 * @module
 */

/**
 * The non-preview panels the toolbar can open as a modal overlay.
 *
 * `environment` is the merged Setup · Variables · Services modal, `pipeline`
 * is the merged Build · Deploy modal (their tabs live inside that one panel —
 * see Workspace's Environment / Pipeline PanelModals), and `storage` is the
 * grouped storage-types modal that absorbed the old Database panel (the
 * database explorer now lives inside its database row). `autoruns` is the
 * hooks/autoruns + running-now + scheduled-tasks panel.
 */
export type WorkspacePanel =
  'environment' | 'apiKeys' | 'pipeline' | 'activity' | 'logs' | 'storage' | 'team' | 'autoruns'

/**
 * Computes the next active panel for a toolbar click: clicking the open panel
 * closes it (→ null); clicking any other panel switches to it (closing whatever
 * was open). This is the entire mutual-exclusion rule — exactly one panel, or
 * none, is ever open.
 * @param current - The currently open panel (or null).
 * @param clicked - The panel whose toolbar button was clicked.
 * @returns The next active panel, or null when toggling the open one closed.
 */
export function nextPanel(
  current: WorkspacePanel | null,
  clicked: WorkspacePanel,
): WorkspacePanel | null {
  return current === clicked ? null : clicked
}
